/**
 * School Blackbaud Data Connection Status API Contract
 *
 * GET /v1/schools/:schoolId/data-connection-status
 *
 * Whether the school's Blackbaud data connection (the admin whose grant the
 * sync runs on) still works, and who must act when it does not (SCH-2418).
 *
 * The dashboard reads it while the school's setup is incomplete. The common
 * cause there is a Marketplace disconnect and reconnect: Blackbaud revokes the
 * connection owner's grant on disconnect, and only a fresh Blackbaud sign-in
 * stores a new one. An admin who stayed signed in never makes that sign-in, so
 * every sync is parked and setup cannot finish.
 *
 * `state`:
 *  - `connected`: the grant works.
 *  - `needs-reconnect`: the grant was revoked or lapsed. The connection owner
 *    must sign out and sign back in with Blackbaud.
 *  - `not-connected`: no admin has connected the school to Blackbaud yet.
 */

import { z } from 'zod'

export const SCHOOL_DATA_CONNECTION_STATES = ['connected', 'needs-reconnect', 'not-connected'] as const
export const SchoolDataConnectionStateSchema = z.enum(SCHOOL_DATA_CONNECTION_STATES)
export type SchoolDataConnectionState = z.infer<typeof SchoolDataConnectionStateSchema>

export const SchoolDataConnectionStatusResponseSchema = z.object({
  state: SchoolDataConnectionStateSchema,
  /** True when the signed-in viewer is the connection owner, so their own sign-in repairs it. */
  viewerIsConnectionOwner: z.boolean(),
  /** The admin whose grant the sync runs on; null when there is none. */
  connectionOwner: z
    .object({
      /** `UsersOnSchools.publicId`. */
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable()
    })
    .nullable()
})
export type SchoolDataConnectionStatusResponse = z.infer<typeof SchoolDataConnectionStatusResponseSchema>
